import { PreLabelStyles } from './../../../shared/components/feature-tile/feature-tile-variants';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  ConsolidatedFeature,
  ConsolidatorDevice,
  CumulatedEnergyProperties,
  EssPhotovoltaicStateProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  PhotovoltaicInsulationResistanceProperties,
  PhotovoltaicInsulationResistanceStatus,
  PhotovoltaicStatusProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import { DevicesStore, FeatureService, InstallationFeaturesStore } from '@store';

import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';

import { TranslateService } from '@ngx-translate/core';
import { takeUntil, tap } from 'rxjs/operators';
import { CustomTranslationsService } from 'src/app/operational-data-common/translations';

@Component({
  selector: 'app-photovoltaics',
  templateUrl: './photovoltaics.component.html',
  styleUrls: ['./photovoltaics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotovoltaicsComponent implements OnInit, OnDestroy {
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly photovoltaicInsulationResistanceStatus = PhotovoltaicInsulationResistanceStatus;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  photovoltaicStatus$: Observable<Feature<PhotovoltaicStatusProperties, never>>;
  photovoltaicCurrentProduction$: Observable<Feature<IsNumberValueProperty, never>>;
  photovoltaicCumulatedEnergy$: Observable<Feature<CumulatedEnergyProperties, never>>;
  photovoltaicInsulationResistance$: Observable<
    Feature<PhotovoltaicInsulationResistanceProperties, never>
  >;
  productionCurrentConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  productionCurrentConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  productionCurrentConsolidatedDeviceId: string;
  exceptionsState: string;
  basicState: string;
  preLabelStyles: PreLabelStyles = {
    fontSize: '18px',
    fontWeight: '500',
  };
  isStatusFeatureFullWidth = true;
  @Input() deviceId: string;
  @Input() deviceSelectId: string;

  constructor(
    public devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private gtmService: GTMService,
    private customTranslationsService: CustomTranslationsService,
    private translateService: TranslateService,
    private featureService: FeatureService
  ) {}

  ngOnInit(): void {
    this.featureService
      .getConsolidatedFeatures(FeatureNames.productionCurrentConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        this.productionCurrentConsolidatedDeviceId = feature?.deviceId;
      });
    this.photovoltaicStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicStatus,
      this.deviceId
    );

    this.photovoltaicCumulatedEnergy$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.photovoltaicCumulatedEnergy,
      this.deviceId
    );
    this.productionCurrentConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.productionCurrentConsolidated
    );
    this.productionCurrentConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.productionCurrentConsolidated,
      this.deviceId,
      this.productionCurrentConsolidatedDeviceId
    );
    this.photovoltaicInsulationResistance$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<PhotovoltaicInsulationResistanceProperties, never>(
        FeatureNames.photovoltaicInsulationResistance,
        this.deviceId
      )
      .pipe(
        tap((feature: Feature<PhotovoltaicInsulationResistanceProperties, never>) => {
          this.isStatusFeatureFullWidth =
            feature?.properties?.status.value !== PhotovoltaicInsulationResistanceStatus.valid;
        })
      );

    // photovoltaic.production.current
    this.photovoltaicCurrentProduction$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.productionCurrent,
      this.deviceId
    );

    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.essPhotovoltaicState, this.deviceId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature: Feature<EssPhotovoltaicStateProperties, never>) => {
        if (feature?.properties) {
          this.photovoltaicStateValues(feature);
        }
      });
  }

  photovoltaicStateValues(feature: Feature<EssPhotovoltaicStateProperties, never>): void {
    const state = (feature.properties?.exception?.value)
      .map((value) => {
        const key = this.customTranslationsService.ESS_PHOTOVOLTAIC_STATES_EXCEPTION[value];

        return key ? this.translateService.instant(key) : '';
      })
      .filter((item) => item !== '')
      .join(', ');
    this.exceptionsState = state ? '(' + state + ')' : '';

    this.basicState = (feature.properties?.basic?.value)
      .map((value) => {
        const key = this.customTranslationsService.ESS_PHOTOVOLTAIC_STATES_BASIC[value];

        return key ? this.translateService.instant(key) : '';
      })
      .filter((item) => item !== '')
      .join(', ');
  }

  expand(): void {
    this.gtmService.track('onPanelExpand', 'Photovoltaics panel', 'installationDetails');
  }

  collapse(): void {
    this.gtmService.track('onPanelCollapse', 'Photovoltaics panel', 'installationDetails');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
